import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { GroupGMetrixTable } from 'components/GroupGMetrixTable/GroupGMetrixTable';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { MostRestrictiveLicense, TransactionApi } from 'apis/TransactionApi';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { ReportFilter } from 'apis/ReportApi';
import { Student } from 'apis/StudentApi';
import { useSuperState } from 'hooks';
import { ListItemCount } from 'types';

import 'components/GroupGMetrixTableDialog/group-gmetrix-table-dialog.scss';

type Option = 'Add' | 'Remove' | 'Duplicate' | undefined;

export interface GroupAssociation {
    associationIds: number[];
    associationName: 'Student' | 'Transaction' | 'Report' | 'Group';
}

export interface GroupGMetrixTableDialogShowOptions {
    actionButtonText?: string;
    customDialogLabel?: string;
    filter?: ReportFilter;
    groupAssociation?: GroupAssociation;
    multiSelect?: boolean;
    objectId?: number;
    option?: Option;
    removeTheseGroups?: number[];
    returnFullGroups?: boolean;
}

export interface GroupGMetrixTableDialogResult {
    groups: StudentGroupWithDetails[];
    ids: number[];
}

export interface GroupGMetrixTableDialogHandle {
    hide: () => void;
    show: (overrides?: GroupGMetrixTableDialogShowOptions) => Promise<GroupGMetrixTableDialogResult | null>;
}

interface GroupGMetrixTableDialogProps {
    actionButtonText?: string;
    currentUserId: number;
    customDialogLabel?: string;
    filter?: ReportFilter;
    groupAssociation?: GroupAssociation;
    multiSelect?: boolean;
    neverDisableSuccessBtn?: boolean;
    objectId?: number;
    option?: Option;
    removeTheseGroups?: number[];
    returnFullGroups?: boolean;
    setGroupKendoCount?: React.Dispatch<React.SetStateAction<ListItemCount>>;
}

export const GroupGMetrixTableDialog = forwardRef<GroupGMetrixTableDialogHandle, GroupGMetrixTableDialogProps>((props, forwardedRef) => {

    const {
        actionButtonText: propActionButtonText,
        currentUserId,
        customDialogLabel: propCustomDialogLabel,
        filter: propFilter,
        groupAssociation: propGroupAssociation,
        multiSelect: propMultiSelect,
        neverDisableSuccessBtn = false,
        objectId: propObjectId,
        option: propOption,
        removeTheseGroups: propRemoveTheseGroups,
        returnFullGroups: propReturnFullGroups,
        setGroupKendoCount
    } = props;

    const [state, setState] = useSuperState({
        groups: [] as StudentGroupWithDetails[],
        loadingGridData: true,
        overrides: null as GroupGMetrixTableDialogShowOptions | null,
        restrictedLicenseObj: { restriction: undefined as MostRestrictiveLicense | undefined, show: false },
        searchTerm: '',
        selectedGroups: [] as StudentGroupWithDetails[]
    });
    const { groups, loadingGridData, overrides, restrictedLicenseObj, searchTerm, selectedGroups } = state;
    const isOpen = overrides !== null;

    const { formatMessage } = useIntl();

    const resolverRef = useRef<((value: GroupGMetrixTableDialogResult | null) => void) | null>(null);

    const resolvedOption = overrides?.option ?? propOption;
    const resolvedMultiSelect = overrides?.multiSelect ?? propMultiSelect;
    const resolvedObjectId = overrides?.objectId ?? propObjectId;
    const resolvedRemoveTheseGroups = overrides?.removeTheseGroups ?? propRemoveTheseGroups;
    const resolvedGroupAssociation = overrides?.groupAssociation ?? propGroupAssociation;
    const resolvedFilter = overrides?.filter ?? propFilter;
    const resolvedActionButtonText = overrides?.actionButtonText ?? propActionButtonText ?? '';
    const resolvedCustomDialogLabel = overrides?.customDialogLabel ?? propCustomDialogLabel;

    const settle = useCallback((value: GroupGMetrixTableDialogResult | null) => {

        const resolve = resolverRef.current;
        resolverRef.current = null;
        setState({
            groups: [],
            loadingGridData: true,
            overrides: null,
            restrictedLicenseObj: { restriction: undefined, show: false },
            searchTerm: '',
            selectedGroups: []
        });
        resolve?.(value);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(forwardedRef, () => ({
        hide: () => settle(null),
        show: (nextOverrides?: GroupGMetrixTableDialogShowOptions) => new Promise<GroupGMetrixTableDialogResult | null>(resolve => {

            resolverRef.current?.(null);
            resolverRef.current = resolve;
            setState({ overrides: nextOverrides ?? {} });

        })
    }), [settle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve pending promise on unmount.
    useEffect(() => () => {

        resolverRef.current?.(null);
        resolverRef.current = null;

    }, []);

    // Pre-select rows matching filter.Value CSV.
    useEffect(() => {

        if (resolvedFilter && resolvedFilter.Value && resolvedFilter.Value.length > 0 && groups.length > 0) {

            const filterSelectedGroups: StudentGroupWithDetails[] = [];
            const nonCsvListOfIds = resolvedFilter.Value.split(',');
            nonCsvListOfIds.forEach(filterValueId => {

                const groupExists = groups.find(group => group.Id === parseInt(filterValueId));
                if (groupExists) {

                    filterSelectedGroups.push(groupExists);

                }

            });
            if (filterSelectedGroups.length > 0) {

                setState({ selectedGroups: filterSelectedGroups });

            }

        }

    }, [groups, resolvedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load grid data on open.
    useEffect(() => {

        if (!isOpen) {

            setState({
                groups: [],
                loadingGridData: false,
                restrictedLicenseObj: { restriction: undefined, show: false },
                selectedGroups: []
            });
            return;

        }
        setState({ loadingGridData: true });
        handleGMetrixTableData(resolvedOption);

    }, [isOpen, resolvedOption]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGMetrixTableData = async (option: Option) => {

        if (!isOpen) return;

        let listOfGroups: StudentGroupWithDetails[] = [];
        if (option === 'Add' || option === 'Duplicate') {

            const allGroups = await StudentGroupApi.GetAll('Partner', -1, resolvedObjectId);
            let groupFilteredList: StudentGroupWithDetails[] = [];
            if (resolvedRemoveTheseGroups) {

                groupFilteredList = allGroups.filter(group => {

                    if (!resolvedRemoveTheseGroups?.includes(group.Id)) {

                        return group;

                    }

                });

            } else {

                groupFilteredList = allGroups;

            }
            listOfGroups = groupFilteredList;

        } else if (resolvedGroupAssociation) {

            if (resolvedGroupAssociation.associationName === 'Student') {

                let listOfGroupsAssociatedWithStudents: StudentGroupWithDetails[] = [];
                for (let i = 0; i < resolvedGroupAssociation.associationIds.length; i++) {

                    const studentId = resolvedGroupAssociation.associationIds[i];
                    const groupsByStudent = await StudentGroupApi.GetAll('Student', studentId).catch(console.error);
                    if (groupsByStudent) {

                        listOfGroupsAssociatedWithStudents = [
                            ...listOfGroupsAssociatedWithStudents,
                            ...groupsByStudent
                        ];

                    }

                }
                listOfGroups = [...new Map(listOfGroupsAssociatedWithStudents.map(item => [item.Id, item])).values()];

            }
            if (resolvedGroupAssociation.associationName === 'Transaction') {

                let listOfGroupsAssociatedWithTransactions: StudentGroupWithDetails[] = [];
                for (let i = 0; i < resolvedGroupAssociation.associationIds.length; i++) {

                    const transactionId = resolvedGroupAssociation.associationIds[i];

                    // TODO: culture ID needs to be dynamic
                    const groupsByTransaction = await StudentGroupApi.GetGroupsByTransactionId(transactionId, 1).catch(
                        console.error
                    );
                    if (groupsByTransaction) {

                        listOfGroupsAssociatedWithTransactions = [
                            ...listOfGroupsAssociatedWithTransactions,
                            ...groupsByTransaction
                        ];

                    }

                }
                listOfGroups = [
                    ...new Map(listOfGroupsAssociatedWithTransactions.map(item => [item.Id, item])).values()
                ];

            }

        }

        setState({ groups: listOfGroups });
        if (setGroupKendoCount && listOfGroups.length > 0) {

            setGroupKendoCount({ count: listOfGroups.length, firstId: listOfGroups[0].Id });

        }
        setState({ loadingGridData: false });

    };

    const runNonReportConfirm = async (option: Option) => {

        if (!resolvedGroupAssociation) return;

        const groupIds = selectedGroups.map(group => group.Id);
        const confirmPayload: GroupGMetrixTableDialogResult = { groups: selectedGroups, ids: groupIds };

        if (option === 'Add') {

            if (groupIds.length > 0) {

                if (resolvedGroupAssociation.associationName === 'Student') {

                    StudentGroupApi.AddStudentToGroup(groupIds, resolvedGroupAssociation.associationIds)
                        .then(success => {

                            const listOfTransactions: number[] = [];
                            groupIds.map(id => {

                                StudentGroupApi.GetTransactionsFromGroup(id)
                                    .then(transactions => {

                                        transactions.map(transaction => {

                                            listOfTransactions.push(transaction.Id);

                                        });

                                    })
                                    .then(() => {

                                        Promise.all(listOfTransactions).then(() => {

                                            if (listOfTransactions.length > 0 && listOfTransactions !== null) {

                                                TransactionApi.AddStudentToTransaction(
                                                    listOfTransactions,
                                                    resolvedGroupAssociation.associationIds
                                                );

                                            }

                                        });

                                    });

                            });

                        })
                        .catch(console.error);

                }
                if (resolvedGroupAssociation.associationName === 'Transaction') {

                    let mostRestrictedLicense: MostRestrictiveLicense | undefined = undefined;
                    const licenseObj = await TransactionApi.GetAllLicenses().catch(console.error);
                    for (let i = 0; i < resolvedGroupAssociation.associationIds.length; i++) {

                        const transactionId = resolvedGroupAssociation.associationIds[i];
                        const content = await TransactionApi.GetLimitedContentByTransactionId(transactionId).catch(
                            console.error
                        );
                        const transaction = await TransactionApi.Get(transactionId).catch(console.error);
                        if (licenseObj && content) {

                            const restrictedLicense = getMostRestrictedLicenseDeliverable(
                                licenseObj.licenses,
                                content,
                                (transaction && transaction.HasXED) === true
                            );
                            if (mostRestrictedLicense === undefined) {

                                mostRestrictedLicense = restrictedLicense;

                            } else if (
                                mostRestrictedLicense.avaliableStudentsAllowed === -1 &&
                                restrictedLicense.avaliableStudentsAllowed !== -1
                            ) {

                                mostRestrictedLicense = restrictedLicense;

                            } else if (
                                mostRestrictedLicense.avaliableStudentsAllowed <
                                restrictedLicense.avaliableStudentsAllowed &&
                                restrictedLicense.avaliableStudentsAllowed !== -1
                            ) {

                                mostRestrictedLicense = restrictedLicense;

                            }

                        }

                    }
                    let studentIds: Student[] = [];
                    for (let i = 0; i < groupIds.length; i++) {

                        const groupId = groupIds[i];
                        const students = await StudentGroupApi.GetAllStudents(groupId).catch(console.error);
                        if (students) {

                            studentIds = [...studentIds, ...students];

                        }

                    }
                    if (
                        mostRestrictedLicense &&
                        (studentIds.length <= mostRestrictedLicense.avaliableStudentsAllowed ||
                            mostRestrictedLicense.avaliableStudentsAllowed === -1)
                    ) {

                        await TransactionApi.AddGroupToTransaction(resolvedGroupAssociation.associationIds, groupIds);
                        await TransactionApi.AddStudentToTransaction(
                            resolvedGroupAssociation.associationIds,
                            studentIds.map(student => student.Id)
                        ).catch(console.error);

                    } else {

                        setState({ restrictedLicenseObj: { restriction: mostRestrictedLicense, show: true } });
                        return;

                    }

                }

            }

        } else if (option === 'Remove') {

            if (groupIds.length > 0) {

                if (resolvedGroupAssociation.associationName === 'Student') {

                    groupIds.forEach(groupId => {

                        let assignedStudents: number[] = [];
                        StudentGroupApi.DeleteStudentFromGroup(groupId, resolvedGroupAssociation.associationIds)
                            .then(success => {

                                resolvedGroupAssociation.associationIds.map(studentId => {

                                    TransactionApi.GetStudentTransactions(studentId)
                                        .then(transactions => {

                                            transactions.map(transaction => {

                                                assignedStudents.push(transaction.StudentContentAccessId);

                                            });

                                        })
                                        .then(() => {

                                            Promise.all(assignedStudents).then(() => {

                                                if (assignedStudents.length > 0 && assignedStudents !== null) {

                                                    TransactionApi.RemoveStudentFromTransaction(assignedStudents);
                                                    assignedStudents = [];

                                                }

                                            });

                                        })
                                        .catch(console.error);

                                });

                            })

                            .catch(console.error);

                    });

                }
                if (resolvedGroupAssociation.associationName === 'Transaction') {

                    await StudentGroupApi.DeleteTransactionFromGroup(resolvedGroupAssociation.associationIds, groupIds);

                }

            }

        }

        settle(confirmPayload);

    };

    return (
        <GMetrixDialog
            actionButtonClick={() => {

                const groupIds = selectedGroups.map(group => group.Id);
                const payload: GroupGMetrixTableDialogResult = {
                    groups: selectedGroups,
                    ids: groupIds
                };

                if (resolvedGroupAssociation?.associationName === 'Report' || resolvedOption === 'Duplicate') {

                    settle(payload);

                } else {

                    runNonReportConfirm(resolvedOption);

                }

            }}
            actionButtonText={
                neverDisableSuccessBtn && selectedGroups.length === 0 ? formatMessage({ id: 'clear_all.bb234ae5', defaultMessage: 'Clear All' }) : resolvedActionButtonText
            }
            dialogLabel={resolvedCustomDialogLabel
                ?? (resolvedOption === 'Add'
                    ? formatMessage({ id: 'add_classrooms.d8c3e49d', defaultMessage: 'Add Classrooms' })
                    : resolvedOption === 'Remove'
                        ? formatMessage({ id: 'remove_classrooms.d97a9ab8', defaultMessage: 'Remove Classrooms' })
                        : resolvedOption === 'Duplicate'
                            ? formatMessage({ id: 'duplicate_classrooms.b14cef1f', defaultMessage: 'Duplicate Classrooms' })
                            : formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' }))}
            actionButtonDisabled={selectedGroups.length === 0 && !neverDisableSuccessBtn}
            onCancel={() => settle(null)}
            show={isOpen}
            size="xl"
        >
            <Container fluid>
                {restrictedLicenseObj && restrictedLicenseObj.restriction && restrictedLicenseObj.show && (
                    <>
                        <Row>
                            <Col>
                                <div className="py-2 d-flex justify-content-center">
                                    <FormattedMessage defaultMessage="Error" id="error.e50d69ad" />
                                    {' '}
                                    -
                                    {restrictedLicenseObj.restriction.license.LicenseName}
                                    {' '}
                                    <FormattedMessage defaultMessage="is being used for some of the content in this Access Code and it only has" id="is_being_used_for_some_of_the_content_in.fedeb56b" />
                                    {restrictedLicenseObj.restriction.avaliableStudentsAllowed}
                                    {' '}
                                    <FormattedMessage defaultMessage="seats available" id="seats_available.d1ad13fb" />
                                    .
                                </div>
                                <div className="py-2 d-flex justify-content-center">
                                    <FormattedMessage defaultMessage="You can only add up to" id="you_can_only_add_up_to.5038baab" />
                                    {' '}
                                    {restrictedLicenseObj.restriction.avaliableStudentsAllowed}
                                    <FormattedMessage defaultMessage="students to this Access Code." id="students_to_this_access_code.92972378" />
                                </div>
                            </Col>
                        </Row>
                        <hr className="hr" />
                    </>
                )}
                <Row>
                    <Col className="pb-3">
                        <GroupGMetrixTable
                            manuallyRenderGroups
                            selectableRows
                            columnData={[
                                { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                                {
                                    cell: ({ dataItem }) => (
                                        <td>
                                            {dataItem.Instructors &&
                                                dataItem.Instructors.length > 0 &&
                                                dataItem.Instructors.map(
                                                    instructor => `${instructor.FirstName} ${instructor.LastName}`
                                                ).join(', ')}
                                        </td>
                                    ),
                                    field: 'Instructors',
                                    title: formatMessage({ id: 'instructors.24923680', defaultMessage: 'Instructors' })
                                },
                                { field: 'StudentCount', title: formatMessage({ id: 'student_count.4d7f96f3', defaultMessage: 'Student Count' }) },
                                { field: 'TransactionCount', title: formatMessage({ id: 'access_code_count.1958d19e', defaultMessage: 'Access Code Count' }) },
                                {
                                    cell: ({ dataItem }) => (
                                        <td>
                                            {new Date(dataItem.DateCreate).toLocaleDateString()}
                                        </td>
                                    ),
                                    field: 'DateCreate',
                                    title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' })
                                }
                            ]}
                            groups={groups.filter(group => {

                                if (
                                    group.Name.toLowerCase().includes(searchTerm.toLocaleLowerCase()) ||
                                    group.Instructors.map(
                                        instructor => `${instructor.FirstName} ${instructor.LastName}`
                                    ).some(string => string.toLowerCase().includes(searchTerm.toLocaleLowerCase()))
                                ) {

                                    return group;

                                }

                            })}
                            allowExport={false}
                            currentUserId={currentUserId}
                            loadingGridData={loadingGridData}
                            multiSelect={resolvedMultiSelect}
                            onSelectedChange={groups => setState({ selectedGroups: groups })}
                            searchTerm={searchTerm}
                            selectedGroups={selectedGroups}
                            setGroups={groups => setState({ groups })}
                            setLoadingGridData={((v: boolean) => setState({ loadingGridData: v })) as React.Dispatch<React.SetStateAction<boolean>>}
                            setSearchTerm={term => setState({ searchTerm: term })}
                            toggleColumns={false}
                        />
                    </Col>
                </Row>
            </Container>
        </GMetrixDialog>
    );

});

GroupGMetrixTableDialog.displayName = 'GroupGMetrixTableDialog';
